import i18n from "i18next";
import { useTranslation } from "react-i18next";

export const RTL_LANGS = ["ar"] as const;

export function isRtlLang(lng: string | undefined | null): boolean {
  return RTL_LANGS.some((l) => (lng ?? "").startsWith(l));
}

/**
 * Mirrors the active language onto <html>, which is what the stylesheets key
 * off. Previously this lived as five separate copies across the pages, one of
 * which had been commented out: so the admin screen silently disagreed with
 * everything else about which way the page ran.
 */
export function applyDirection(lng: string): void {
  if (typeof document === "undefined") return;
  const rtl = isRtlLang(lng);
  const html = document.documentElement;

  html.setAttribute("lang", rtl ? "ar" : "en");
  html.setAttribute("dir", rtl ? "rtl" : "ltr");
  /* Retained so any remaining `body.rtl` selectors keep matching. */
  document.body.classList.toggle("rtl", rtl);
}

export function toggleLanguage(): void {
  const next = isRtlLang(i18n.language) ? "en" : "ar";
  i18n.changeLanguage(next);
  try {
    localStorage.setItem("lang", next);
  } catch {
    /* Persisting the choice is a nicety, not a requirement. */
  }
  applyDirection(next);
}

/** Direction-aware helpers for components that need to branch on it. */
export function useDirection() {
  const { i18n: instance } = useTranslation();
  const rtl = isRtlLang(instance.language);
  return { isRtl: rtl, dir: (rtl ? "rtl" : "ltr") as "rtl" | "ltr" };
}
